import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox, faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * Shared table primitives built on Tailwind (prefix `tw-`, see
 * tailwind.config.js), used by both the admin dashboard and the buyer/seller
 * dashboards.
 *
 * These exist to fix the alignment bug that kept recurring across the app's
 * tables: headers and cells each carried their own ad-hoc padding, so a
 * column's header and its data drifted apart as soon as content lengths
 * varied. Here every header and cell shares one padding/alignment rule, and
 * `table-fixed` plus an explicit <colgroup> (via the `columns` prop) pins each
 * column to a known width — alignment is correct by construction rather than
 * by hand-tuning each table.
 *
 * Dark mode uses Tailwind's `dark:` variant, wired in tailwind.config.js to
 * the app's own toggle classes, so no isDarkMode prop threading is needed.
 *
 * Usage:
 *   <TableCard>
 *     <DataTable columns={[{ width: '26%' }, { width: '18%' }]}>
 *       <THead><Th>Name</Th><Th align="right">Total</Th></THead>
 *       <tbody>
 *         <Tr><Td>…</Td><Td align="right">…</Td></Tr>
 *       </tbody>
 *     </DataTable>
 *   </TableCard>
 */

export const TableCard = ({ children, className = '' }) => (
  <div
    className={`tw-rounded-2xl tw-overflow-hidden tw-backdrop-blur-xl tw-shadow-[0_8px_32px_rgba(0,0,0,0.12)]
      tw-bg-white/95 tw-border tw-border-green-600/10
      dark:tw-bg-zinc-900/70 dark:tw-border-green-500/15 ${className}`}
  >
    {children}
  </div>
);

export const DataTable = ({ columns, children, minWidth = '720px' }) => (
  <div className="tw-overflow-x-auto">
    <table className="tw-w-full tw-table-fixed tw-border-collapse" style={{ minWidth }}>
      {columns && (
        <colgroup>
          {columns.map((col, i) => (
            <col key={i} style={{ width: col.width || 'auto' }} />
          ))}
        </colgroup>
      )}
      {children}
    </table>
  </div>
);

export const THead = ({ children }) => (
  <thead>
    <tr className="tw-bg-green-600/[0.06] tw-border-b tw-border-green-600/15 dark:tw-bg-green-500/10 dark:tw-border-green-500/25">
      {children}
    </tr>
  </thead>
);

export const Th = ({ children, align = 'left', className = '' }) => (
  <th
    className={`tw-px-5 tw-py-3.5 tw-text-[0.7rem] tw-font-bold tw-uppercase tw-tracking-wider
      tw-text-green-600 dark:tw-text-green-400
      ${align === 'right' ? 'tw-text-right' : align === 'center' ? 'tw-text-center' : 'tw-text-left'} ${className}`}
  >
    {children}
  </th>
);

export const Tr = ({ children, className = '', ...props }) => (
  <tr
    className={`tw-border-b tw-border-black/5 dark:tw-border-white/5 tw-transition-colors
      hover:tw-bg-green-600/[0.04] dark:hover:tw-bg-green-500/[0.06] ${className}`}
    {...props}
  >
    {children}
  </tr>
);

// `overflow-hidden` + `text-ellipsis` used to sit here unconditionally. On a
// plain text node that does nothing useful — CSS ellipsis only ever kicks in
// alongside `white-space: nowrap`, which this never set, so long text just
// wrapped anyway — and on an atomic inline-flex child like <Badge> (which
// can't itself break mid-text) it actively hurt: a column too narrow for the
// badge's natural width got its box hard-clipped by the td's overflow-hidden,
// rendering as a stray "." or "..." depending on exactly where the clip line
// fell. Cells that genuinely need single-line truncation opt in per-cell
// (wrap the content in `tw-truncate`, as the Customer column already does)
// rather than every cell paying for a rule that only worked by accident.
export const Td = ({ children, align = 'left', className = '' }) => (
  <td
    className={`tw-px-5 tw-py-3.5 tw-text-sm tw-align-middle
      tw-text-zinc-800 dark:tw-text-zinc-100
      ${align === 'right' ? 'tw-text-right' : align === 'center' ? 'tw-text-center' : 'tw-text-left'} ${className}`}
  >
    {children}
  </td>
);

const BADGE_TONES = {
  green: 'tw-bg-green-500/15 tw-text-green-600 dark:tw-text-green-400',
  red: 'tw-bg-red-500/15 tw-text-red-600 dark:tw-text-red-400',
  amber: 'tw-bg-amber-500/15 tw-text-amber-600 dark:tw-text-amber-400',
  blue: 'tw-bg-blue-500/15 tw-text-blue-600 dark:tw-text-blue-400',
  neutral: 'tw-bg-slate-500/15 tw-text-slate-600 dark:tw-text-slate-400',
};

export const Badge = ({ tone = 'neutral', icon, children }) => (
  <span
    className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-whitespace-nowrap ${
      BADGE_TONES[tone] || BADGE_TONES.neutral
    }`}
  >
    {icon && <FontAwesomeIcon icon={icon} className="tw-text-[0.65rem]" />}
    {children}
  </span>
);

/**
 * Maps a backend status string to a badge tone, so every table across the app
 * colours the same status the same way instead of each one hardcoding hexes.
 */
export const statusTone = (status) => {
  const s = String(status || '').toLowerCase();
  if (['completed', 'delivered', 'active', 'approved', 'resolved', 'paid', 'verified', 'released', 'success'].includes(s)) return 'green';
  if (['pending', 'processing', 'shipped', 'in_transit', 'reviewing', 'investigating', 'held', 'pending_seller_review', 'pending_initiator_review'].includes(s)) return 'amber';
  if (['cancelled', 'failed', 'rejected', 'refunded', 'declined', 'banned', 'disputed'].includes(s)) return 'red';
  if (['inactive', 'draft', 'expired', 'closed'].includes(s)) return 'neutral';
  return 'blue';
};

export const TableEmpty = ({ colSpan, message = 'Nothing here yet' }) => (
  <tr>
    <td colSpan={colSpan} className="tw-py-16 tw-text-center">
      <FontAwesomeIcon icon={faInbox} className="tw-text-3xl tw-mb-3 tw-text-zinc-300 dark:tw-text-zinc-700" />
      <p className="tw-text-sm tw-font-medium tw-text-zinc-400 dark:tw-text-zinc-500">{message}</p>
    </td>
  </tr>
);

export const TableLoading = ({ colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="tw-py-16 tw-text-center">
      <FontAwesomeIcon icon={faSpinner} spin className="tw-text-2xl tw-text-green-500" />
    </td>
  </tr>
);

export const IconBtn = ({ onClick, title, tone = 'neutral', disabled, children }) => {
  const toneClasses =
    tone === 'red'
      ? 'tw-bg-red-500/10 tw-text-red-500 hover:tw-bg-red-500/20'
      : tone === 'green'
      ? 'tw-bg-green-500/10 tw-text-green-500 hover:tw-bg-green-500/20'
      : 'tw-bg-black/5 tw-text-zinc-600 hover:tw-bg-black/10 dark:tw-bg-white/5 dark:tw-text-zinc-300 dark:hover:tw-bg-white/10';
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`tw-w-8 tw-h-8 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-inline-flex tw-items-center tw-justify-center tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed ${toneClasses}`}
    >
      {children}
    </button>
  );
};
